/**
 * Pick a game, then play it.
 *
 * WHY THERE IS A SECOND SCREEN AT ALL. The home screen had grown to fifteen
 * controls, and the reason was not carelessness -- every one of them was a
 * real thing somebody might want. What was missing was an order. A visitor met
 * three modes, three difficulties, nine courses, a daily, a group, a practice
 * match, two link buttons and an account form, all at once, and had to work
 * out which of them was the question before answering any of it.
 *
 * So: home asks one thing, and this screen asks the next. Two taps to a match,
 * which is what changing mode already cost, and the tap that used to be a
 * guess is now a choice with the numbers printed on it.
 *
 * THE COUNTS AND THE DAILY PIPS ARE THE POINT, not decoration. A mode with
 * "34/198 found" beside it is a place with something left in it; a mode with
 * an unplayed daily is a reason to go there today rather than tomorrow.
 * Testers reported not realising the other modes existed at all -- a screen
 * that shows what is waiting inside each of them is a better answer to that
 * than a bigger label.
 *
 * DIFFICULTY AND PACE ARE HERE AND ARE NOT A THIRD STEP. They sit under one
 * summary line that says what you will get. A returning player never has to
 * open it; the person who needs a slower clock opens it once and this browser
 * remembers.
 */

import { useEffect, useState } from 'react';
import { allCountries } from '@geogame/data';
import { COURSES } from '@geogame/rules/courses';
import { DAILY_TIER, GAME_MODES, PACES, TIERS, countryPromptIsClue } from '@geogame/shared';
import type { GameMode, Pace, Tier } from '@geogame/shared';

import { fetchCoverage, fetchDailyStatus } from '../lib/api';
import { tierLabel } from '../lib/format';
import { navigate } from '../lib/router';
import { useGame } from '../store/gameStore';
import { identityToken, persistPace, persistedPace } from '../store/storage';

const MODE_LABEL: Record<GameMode, string> = {
  country: 'Countries',
  city: 'Cities',
  state: 'US States',
};

const MODE_BLURB: Record<GameMode, string> = {
  country: 'Find a whole country, then answer questions about it.',
  city: 'Pin a city, then answer questions about it.',
  state: 'Find a state, then find its capital.',
};

/** How many places a mode can ever show you. Only countries are known here. */
const TOTALS: Partial<Record<GameMode, number>> = {
  country: allCountries().length,
};

export function PlayScreen(): React.JSX.Element {
  const startMatch = useGame((s) => s.startMatch);
  const storedTier = useGame((s) => s.tier);

  // Local, like the home screen's: `startMatch` is what commits a choice to
  // the store, so holding it here means backing out costs nothing.
  const [tier, setTier] = useState<Tier>(storedTier);
  const [courseId, setCourseId] = useState<string>('');
  const [pace, setPace] = useState<Pace>(() => persistedPace());
  const [found, setFound] = useState<Partial<Record<GameMode, number>>>({});
  const [dailyPlayed, setDailyPlayed] = useState<Partial<Record<GameMode, boolean>>>({});
  const [setupOpen, setSetupOpen] = useState(false);
  /**
   * The mode the player has picked but not yet committed to.
   *
   * A CARD USED TO START A MATCH ON THE TAP. That reads fine when the settings
   * happen to be the ones you want and badly when they are not: the difficulty
   * and the clock live one disclosure below the cards, so it was possible to
   * be six rounds into Hardest before noticing you had never opened it. The
   * tap now says which game, and the screen it lands on says under what terms.
   *
   * A STEP, NOT A ROUTE, because it is a question about the tap you just made
   * rather than a place. Backing out is one button and leaves nothing behind.
   */
  const [confirming, setConfirming] = useState<GameMode | null>(null);

  /*
   * Both reads are for their own sake and neither blocks a button. A browser
   * that has never played has no token and no progress, and a Play card that
   * waited on a number nobody has yet would be slower than the screen it
   * replaced.
   */
  useEffect(() => {
    const token = identityToken();
    let live = true;

    void fetchDailyStatus(token).then((played) => {
      if (live) setDailyPlayed(played);
    });

    if (token !== null) {
      void Promise.all(
        GAME_MODES.map(async (m) => {
          const rows = await fetchCoverage(m, token).catch(() => []);
          return [m, rows.filter((r) => r.correct > 0).length] as const;
        }),
      ).then((pairs) => {
        if (!live) return;
        const next: Partial<Record<GameMode, number>> = {};
        for (const [m, n] of pairs) if (n > 0) next[m] = n;
        setFound(next);
      });
    }

    return () => {
      live = false;
    };
  }, []);

  const choosePace = (next: Pace): void => {
    setPace(next);
    persistPace(next);
  };

  /**
   * EXPERT IS A COUNTRY DIFFICULTY. It differs from Hard only in describing
   * the country instead of naming it, and Cities and US States already do
   * exactly that at Hard -- so in those modes it is not offered, and a
   * browser that remembered it from a Countries game plays Hard instead.
   */
  const tierFor = (mode: GameMode | null): Tier =>
    mode !== null && mode !== 'country' && countryPromptIsClue(tier) ? 'cartographer' : tier;
  const tiersFor = (mode: GameMode | null): readonly Tier[] =>
    TIERS.filter((t) => !countryPromptIsClue(t) || mode === null || mode === 'country');

  const play = (mode: GameMode): void => {
    startMatch({
      mode: 'quick',
      tier: tierFor(mode),
      gameMode: mode,
      pace,
      ...(mode === 'country' && courseId ? { courseId } : {}),
    });
  };

  /**
   * The daily ignores the tier and the course, deliberately.
   *
   * Everybody gets the same puzzle at `DAILY_TIER` -- that is the whole reason
   * a daily score is worth saying out loud. Starting one from a screen showing
   * "Hardest" would otherwise silently promise a harder daily than the board
   * is scored on.
   */
  const playDaily = (mode: GameMode): void => {
    setTier(DAILY_TIER);
    startMatch({ mode: 'daily', tier: DAILY_TIER, gameMode: mode });
  };

  const courseName = COURSES.find((c) => c.id === courseId)?.name ?? null;

  /**
   * What you are about to get, in one line.
   *
   * THE COURSE IS ONLY NAMED WHEN IT APPLIES. `play` drops it in any mode but
   * Countries, so a Cities summary reading "Standard · The Americas" would be
   * describing a filter the match is about to throw away.
   */
  const summaryFor = (mode: GameMode | null): string =>
    [
      tierLabel(tierFor(mode)),
      pace === 'relaxed' ? 'relaxed clock' : null,
      mode === 'country' || mode === null ? courseName : null,
    ]
      .filter((p): p is string => p !== null)
      .join(' · ');

  const summary = summaryFor(null);

  /**
   * The same controls on both steps, defined once.
   *
   * The confirm step exists to show what you are about to play, and a review
   * you cannot act on is a dead end -- so the settings there have to be the
   * real ones rather than a read-only echo of them. Two copies would be two
   * places for a control to be added to only one.
   *
   * TAKES THE MODE, because not every setting applies to every mode. A course
   * narrows the country pool and `generateMatch` throws if handed one in any
   * other mode, so `play` drops it -- which meant Cities and US States offered
   * a row of nine courses that were discarded the moment Start was pressed.
   * Reported as "the user chooses a course on these game modes but it impacts
   * nothing", which is the worst kind of control: one that answers.
   *
   * NULL means no mode is chosen yet, which is the pre-set panel on the mode
   * list. Courses are hidden there too: the only place they can honestly be
   * offered is a screen that has already said Countries.
   */
  const settingsControls = (mode: GameMode | null): React.JSX.Element => (
    <>
        <div className="gg-course-row" role="radiogroup" aria-label="Difficulty">
          {tiersFor(mode).map((t) => (
            <button
              key={t}
              type="button"
              role="radio"
              aria-checked={tierFor(mode) === t}
              className="gg-chip"
              data-selected={tierFor(mode) === t ? 'true' : 'false'}
              onClick={() => setTier(t)}
            >
              {tierLabel(t)}
            </button>
          ))}
        </div>
        {mode === 'country' || mode === null ? (
          <p className="gg-note">
            Expert describes the country instead of naming it. You get a few extra seconds
            to read the clue, and then the same questions as Hard.
          </p>
        ) : null}

        {/*
          PACE IS NOT A FOURTH DIFFICULTY. How hard the questions are and
          how long you get to answer them are different questions, and
          somebody who knows their geography and reads slowly wants the
          hard questions at a kind speed. Remembered on this browser,
          because the person who needs it should have to find it once.
        */}
        <div className="gg-course-row" role="radiogroup" aria-label="Pace">
          {PACES.map((pc) => (
            <button
              key={pc}
              type="button"
              role="radio"
              aria-checked={pace === pc}
              className="gg-chip"
              data-selected={pace === pc ? 'true' : 'false'}
              onClick={() => choosePace(pc)}
            >
              {pc === 'relaxed' ? 'Relaxed clock' : 'Normal clock'}
            </button>
          ))}
        </div>
        <p className="gg-note">
          A relaxed clock gives every round about a minute. The questions are the same, and
          what you find still counts towards your map and your head-to-head record. Relaxed
          rounds stay off the weekly boards, because a minute and fifteen seconds are not the
          same race.
        </p>

        {/*
          COUNTRIES ONLY, and hidden rather than disabled anywhere else. A
          course narrows the country pool; `generateMatch` throws if handed one
          in another mode, so `play` drops it. Showing the row on Cities was
          offering nine choices that were discarded on Start.
        */}
        {mode === 'country' ? (
          <>
            <div className="gg-course-row" role="radiogroup" aria-label="Course">
              <button
                type="button"
                role="radio"
                aria-checked={courseId === ''}
                className="gg-chip"
                data-selected={courseId === '' ? 'true' : 'false'}
                onClick={() => setCourseId('')}
              >
                Anywhere
              </button>
              {COURSES.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  role="radio"
                  aria-checked={courseId === c.id}
                  className="gg-chip"
                  data-selected={courseId === c.id ? 'true' : 'false'}
                  onClick={() => setCourseId(courseId === c.id ? '' : c.id)}
                  title={c.description}
                >
                  {c.name}
                </button>
              ))}
            </div>
            <p className="gg-note">A course narrows which countries can come up.</p>
          </>
        ) : null}
    </>
  );

  if (confirming !== null) {
    return (
      <main className="gg-screen gg-scroll">
        <header className="gg-hero">
          <h1 className="gg-title">Check your settings</h1>
          <p className="gg-subtitle">
            {MODE_LABEL[confirming]} · {MODE_BLURB[confirming]}
          </p>
        </header>

        <section className="gg-section gg-actions">
          <div className="gg-confirm-summary">
            <span className="gg-confirm-mode">{MODE_LABEL[confirming]}</span>
            <span className="gg-confirm-terms">{summaryFor(confirming)}</span>
          </div>

          {settingsControls(confirming)}
        </section>

        <section className="gg-section gg-actions">
          <button
            type="button"
            className="gg-btn gg-btn-primary gg-btn-xl"
            onClick={() => play(confirming)}
          >
            Start
          </button>
          {/*
            Back to the modes rather than back to the home screen: changing
            your mind here is almost always about which game, not about
            whether to play one.
          */}
          <button type="button" className="gg-btn" onClick={() => setConfirming(null)}>
            Pick a different mode
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="gg-screen gg-scroll">
      <header className="gg-hero">
        <h1 className="gg-title">What are we playing?</h1>
        <p className="gg-subtitle">
          Find the place on the globe. Then keep what you won, or bet it on a question.
        </p>
      </header>

      <section className="gg-section gg-modecards">
        {GAME_MODES.map((m) => {
          const count = found[m];
          const total = TOTALS[m];
          const dailyReady = dailyPlayed[m] === false;
          return (
            <div key={m} className="gg-modecard">
              <button
                type="button"
                className="gg-modecard-main"
                onClick={() => setConfirming(m)}
              >
                <span className="gg-modecard-name">{MODE_LABEL[m]}</span>
                <span className="gg-modecard-blurb">{MODE_BLURB[m]}</span>
                <span className="gg-modecard-count tabular-nums">
                  {count === undefined
                    ? 'not started'
                    : `${count}${total === undefined ? '' : `/${total}`} found`}
                </span>
              </button>
              {/*
                The daily is a SECOND target on the card rather than a separate
                list of three. It is the same game in the same mode; what makes
                it different is that everybody alive is playing that exact
                puzzle today, and a pip next to the mode says that in less
                space than a sentence.
              */}
              <button
                type="button"
                className="gg-modecard-daily"
                data-ready={dailyReady ? 'true' : 'false'}
                onClick={() => playDaily(m)}
              >
                Daily
                <span className="gg-modecard-dailynote">
                  {dailyPlayed[m] === true ? 'played' : dailyReady ? 'new today' : ''}
                </span>
              </button>
            </div>
          );
        })}
      </section>

      {/*
        BACK ROADS is not a GameMode: it has no tier, no clock, no opponent and
        no gateway, so it does not belong in the grid above, where every card
        starts a match. It is a fourth card of its own shape, and a link.
      */}
      <section className="gg-section">
        <button
          type="button"
          className="gg-backroads-card"
          onClick={() => navigate({ name: 'backroads', tripId: null })}
        >
          <span className="gg-backroads-kicker">
            <span className="gg-beta">Beta</span> Untimed, offline, nothing to lose
          </span>
          <span className="gg-backroads-name">Back Roads</span>
          <span className="gg-backroads-blurb">
            A drive through New York State in a chosen year: the Erie Canal, Route 9 before the
            Northway, the towns under the reservoirs. Find each stop, answer one question, read the
            postcard.
          </span>
        </button>
      </section>

      <section className="gg-section gg-actions">
        <button
          type="button"
          className="gg-setup"
          aria-label="Change difficulty, pace and course"
          aria-expanded={setupOpen}
          onClick={() => setSetupOpen((v) => !v)}
        >
          <span className="gg-setup-label">{summary}</span>
          <span className="gg-setup-action">{setupOpen ? 'Done' : 'Change'}</span>
        </button>

        {setupOpen ? (
          <>
            {settingsControls(null)}
          </>
        ) : null}
      </section>

      <section className="gg-section gg-section-quiet">
        <button type="button" className="gg-btn" onClick={() => navigate({ name: 'home' })}>
          Back
        </button>
      </section>
    </main>
  );
}
